import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../lib/cn";

/* -------------------------------------------------------------------------- */
/* Module-level toast store — usable outside React (mutation callbacks, etc.)  */
/* -------------------------------------------------------------------------- */

const TOAST_LIMIT = 5;
const TOAST_EXIT_MS = 200;

export type ToastVariant = "default" | "success" | "warning" | "destructive";

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Auto-dismiss delay in ms; falls back to the Toaster default. */
  duration?: number;
}

interface ToastRecord extends ToastOptions {
  id: string;
  open: boolean;
}

export interface ToastState {
  toasts: ToastRecord[];
}

let memoryState: ToastState = { toasts: [] };
let counter = 0;
const listeners = new Set<() => void>();
const removalTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function setState(next: ToastState): void {
  memoryState = next;
  listeners.forEach((listener) => listener());
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

function getSnapshot(): ToastState {
  return memoryState;
}

/**
 * Imperative toast API. Safe to call from anywhere, including non-React code
 * (query mutation callbacks, error interceptors, …). Requires a mounted
 * <Toaster /> to actually render.
 */
export function toast(options: ToastOptions): { id: string; dismiss: () => void } {
  counter += 1;
  const id = String(counter);
  const record: ToastRecord = { variant: "default", ...options, id, open: true };
  setState({ toasts: [record, ...memoryState.toasts].slice(0, TOAST_LIMIT) });
  return { id, dismiss: () => dismissToast(id) };
}

/** Dismiss one toast by id, or all toasts when called without an id. */
export function dismissToast(id?: string): void {
  const ids = id === undefined ? memoryState.toasts.map((t) => t.id) : [id];
  setState({
    toasts: memoryState.toasts.map((t) => (ids.includes(t.id) ? { ...t, open: false } : t)),
  });
  for (const toastId of ids) {
    if (removalTimeouts.has(toastId)) continue;
    removalTimeouts.set(
      toastId,
      setTimeout(() => {
        removalTimeouts.delete(toastId);
        setState({ toasts: memoryState.toasts.filter((t) => t.id !== toastId) });
      }, TOAST_EXIT_MS),
    );
  }
}

/** Subscribe to the toast store from React (used by Toaster; exported for tests/devtools). */
export function useToasts(): ToastState {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/* -------------------------------------------------------------------------- */
/* Toaster — mount once near the app root                                      */
/* -------------------------------------------------------------------------- */

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start justify-between gap-3 overflow-hidden rounded-md border p-4 pe-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
  {
    variants: {
      variant: {
        default: "border-border bg-popover text-popover-foreground",
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-transparent bg-warning text-warning-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface ToasterProps extends VariantProps<typeof toastVariants> {
  /**
   * Accessible label for each toast's close button.
   * Defaults to English "Close" — consumers must pass a translated string.
   */
  closeLabel?: string;
  /** Default auto-dismiss delay in ms for toasts that do not specify one. */
  duration?: number;
}

export function Toaster({ closeLabel = "Close", duration = 5000 }: ToasterProps) {
  const { toasts } = useToasts();
  // Swipe-to-dismiss should move toward the viewport edge the toasts sit on,
  // which flips with the document direction.
  const isRtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";

  return (
    <ToastPrimitives.Provider swipeDirection={isRtl ? "left" : "right"} duration={duration}>
      {toasts.map((t) => (
        <ToastPrimitives.Root
          key={t.id}
          open={t.open}
          duration={t.duration}
          onOpenChange={(open) => {
            if (!open) dismissToast(t.id);
          }}
          className={cn(toastVariants({ variant: t.variant }))}
        >
          <div className="grid gap-1">
            <ToastPrimitives.Title className="text-sm font-semibold">
              {t.title}
            </ToastPrimitives.Title>
            {t.description ? (
              <ToastPrimitives.Description className="text-sm opacity-90">
                {t.description}
              </ToastPrimitives.Description>
            ) : null}
          </div>
          <ToastPrimitives.Close
            className="absolute end-2 top-2 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={closeLabel}
          >
            <X className="size-4" aria-hidden="true" />
          </ToastPrimitives.Close>
        </ToastPrimitives.Root>
      ))}
      <ToastPrimitives.Viewport
        className={cn(
          "fixed bottom-0 end-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 outline-none sm:max-w-[420px]",
        )}
      />
    </ToastPrimitives.Provider>
  );
}
